import { NgClass, NgOptimizedImage } from '@angular/common';
import { Component, HostBinding, Input, OnChanges } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import {
    EventModel,
    EventParticipant,
    EventParticipantType,
    ParticipantInfo,
    ScoreboardType,
    SpecialChar,
} from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { FavouritesConfig, ImagesConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { FavouriteParticipantToggleComponent } from '@frontend/sports/favourites/feature/participant-toggle';
import { ImageRootPath } from '@frontend/sports/participant-image/feature';
import { UtilsService } from '@frontend/vanilla/core';

export interface ParticipantViewModel {
    participantName: string;
    logoIsLoaded: boolean;
    participantLogoUrl: string;
    shouldTruncateParticipantName: boolean;
}

@Component({
    selector: 'ms-scoreboard-participant',
    templateUrl: './scoreboard-participant.component.html',
    imports: [NgOptimizedImage, NgClass, FavouriteParticipantToggleComponent, AutomationModule],
})
export class ScoreboardParticipantComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() participant: EventParticipant;
    @Input() showIndicator = true;
    @Input() isNewEDPScoreboard = false;
    @Input() participantInfo?: ParticipantInfo;
    @HostBinding('class.doubles') isDoubleParticipant = false;
    @HostBinding('class.show-pitcher-record') isPitcherOrRecordsAvailable = false;
    doubleParticipantName?: string[];
    showCountryName = true;
    isShowRank = false;

    vm: ParticipantViewModel = {
        participantName: '',
        logoIsLoaded: false,
        participantLogoUrl: '',
        shouldTruncateParticipantName: false,
    };

    showAwayIndicator: boolean;
    private readonly wordsRegEx = new RegExp(String.raw`^(\w*)$`);

    private readonly animationSports = [SportConstant.Basketball, SportConstant.Icehockey, SportConstant.Tennis];

    constructor(
        public favourites: FavouritesConfig,
        private config: ImagesConfig,
        private utilsService: UtilsService,
        private shortTeamNamesService: ShortTeamNamesService,
        public sitecore: Sitecore,
    ) {}

    ngOnChanges(changes: ISimpleChanges<ScoreboardParticipantComponent>): void {
        this.showAwayIndicator =
            this.event.viewType === FixtureViewType.American &&
            this.participant.type === EventParticipantType.Away &&
            this.showIndicator &&
            !this.isNewEDPScoreboard;

        if (
            changes.participant &&
            (changes.participant.firstChange || changes.participant.currentValue.id !== changes.participant.previousValue.id)
        ) {
            const participantName = this.getParticipantName();
            const words = this.wordsRegEx.exec(participantName)!;
            this.vm = {
                participantName,
                logoIsLoaded: false,
                participantLogoUrl:
                    (this.participant.image &&
                        this.participant.image.logo &&
                        this.utilsService.format(
                            this.config.participantLogoUrl,
                            this.participant.image?.isParticipantProfile ? ImageRootPath.Prod : ImageRootPath.Live,
                            this.event.sport.id,
                            this.participant.image.logo.split('.')[0],
                        )) ||
                    '',
                shouldTruncateParticipantName: !!words?.length,
            };
        }
        this.isDoubleParticipant =
            this.isNewEDPScoreboard &&
            this.vm.participantName.includes(SpecialChar.Slash) &&
            (this.event.scoreboard.type === ScoreboardType.SetGame || this.event.scoreboard.type === ScoreboardType.PreMatchPairGame);
        if (this.isDoubleParticipant) {
            this.doubleParticipantName = this.vm.participantName.split(SpecialChar.Slash);
        }
        this.isPitcherOrRecordsAvailable = !!(this.participantInfo?.pitcher || this.participantInfo?.winLossStats);
        this.showCountryName = !(
            this.isNewEDPScoreboard &&
            (this.isDoubleParticipant ||
                (this.event.scoreboard.started &&
                    this.event.scoreboard.type !== ScoreboardType.LeaderboardGame &&
                    this.event.scoreboard.type !== ScoreboardType.Darts &&
                    this.event.scoreboard.type !== ScoreboardType.Snooker &&
                    this.event.scoreboard.type !== ScoreboardType.SetGame))
        );
        this.isShowRank = !!this.participantInfo?.rank;
    }

    get isLiveAnimationSport(): boolean {
        return this.event.scoreboard.started && this.animationSports.indexOf(this.event.sport.id) > -1;
    }

    get showFavouriteToggle(): boolean {
        return !this.isNewEDPScoreboard && this.favourites.isFavouritesEnabled;
    }

    getParticipantCountry(): string {
        return this.participant.country!.replace(/-/g, '\u2011');
    }

    private getParticipantName(): string {
        if (
            this.shortTeamNamesService.shouldUseShortName(this.participant, this.event.sport.id, this.event.league.id, ShortNameModule.EdpScoreboard)
        ) {
            const name = this.participant.shortName!;

            return this.shortTeamNamesService.clearShortName(name);
        }

        return this.participant.name;
    }

    logoLoad(): void {
        this.vm.logoIsLoaded = true;
    }
}
